import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DealStage, VisitAction, VisitActionKind, VisitDto, VisitTarget } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { Icon } from '../icons';
import { dayLabel, fmtTime, localDay, todayKey } from '../format';
import { Empty, ErrorBanner, Field, Modal, More, Panel, Pill } from '../ui';
import { DateInput, TimeInput } from '../pickers';
import { SlotPicker, addMin, useBusy } from '../slots';
import { ask } from '../dialogs';

interface Person {
  id: number;
  name: string;
  role: string;
}
interface Meta {
  people: Person[];
  targets: VisitTarget[];
  ai: { transcribe: boolean; summary: boolean };
}

/** Da dove si apre il modulo: trattativa, contatto o cliente già scelti. */
export interface VisitPrefill {
  dealId?: number | null;
  contactId?: number | null;
  clientId?: number | null;
}

const pad = (n: number) => String(n).padStart(2, '0');
const hm = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

/** Spiega cosa resta occupato in calendario: dall'uscita dall'ufficio al rientro. */
function travelHint(start: string, end: string, travel: number): string {
  if (!travel) return 'In calendario resti occupato solo il tempo dal cliente';
  const ok = (t: string) => /^\d{2}:\d{2}$/.test(t);
  if (!ok(start)) return "Il ritorno dura uguale: in calendario resti occupato dall'uscita al rientro";
  const toM = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
  const fmt = (m: number) => {
    const x = ((m % 1440) + 1440) % 1440;
    return `${pad(Math.floor(x / 60))}:${pad(x % 60)}`;
  };
  const s = toM(start);
  const e = ok(end) ? toM(end) : s + 60;
  return `In calendario resti occupato dalle ${fmt(s - travel)} alle ${fmt(e + travel)}, viaggio compreso`;
}

const REMINDERS: { v: number | null; label: string }[] = [
  { v: null, label: 'Nessuno' },
  { v: 30, label: '30 minuti prima' },
  { v: 60, label: '1 ora prima' },
  { v: 120, label: '2 ore prima' },
  { v: 1440, label: '1 giorno prima' },
];

const TRAVEL: { v: number; label: string }[] = [
  { v: 0, label: 'Nessuno (siamo già lì)' },
  { v: 15, label: '15 minuti' },
  { v: 30, label: '30 minuti' },
  { v: 45, label: '45 minuti' },
  { v: 60, label: '1 ora' },
  { v: 90, label: '1 ora e ½' },
  { v: 120, label: '2 ore' },
];

const KINDS: { v: VisitActionKind; label: string }[] = [
  { v: 'call', label: 'Chiamata' },
  { v: 'mail', label: 'Email' },
  { v: 'quote', label: 'Preventivo' },
  { v: 'visit', label: 'Visita' },
  { v: 'task', label: 'Da fare' },
];

const STAGES: { v: DealStage; label: string }[] = [
  { v: 'new', label: 'Nuova' },
  { v: 'contacted', label: 'Contattata' },
  { v: 'quote', label: 'Preventivo' },
  { v: 'won', label: 'Vinta' },
  { v: 'lost', label: 'Persa' },
];

export function useVisitMeta(ctx: Ctx): [Meta | null, string | null] {
  const [meta, setMeta] = useState<Meta | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    ctx.api
      .get<Meta>('/api/visits/meta')
      .then(setMeta)
      .catch((e) => setError((e as Error).message));
  }, [ctx.api]);
  return [meta, error];
}

const withWho = (v: VisitDto) => v.clientName ?? v.contactName ?? v.dealName ?? null;

function aiPill(v: VisitDto) {
  if (v.status === 'cancelled') return <Pill tone="bad">Annullata</Pill>;
  if (v.aiStatus === 'ready') return <Pill tone={v.actionsAppliedAt ? 'good' : 'info'}>{v.actionsAppliedAt ? 'Chiusa' : 'Riassunto pronto'}</Pill>;
  if (v.aiStatus === 'waiting') return <Pill tone="warn">{v.audio.open ? 'In registrazione' : 'In lavorazione'}</Pill>;
  if (v.aiStatus === 'error') return <Pill tone="bad">Da rivedere</Pill>;
  if (v.status === 'done') return <Pill tone="soft">Fatta</Pill>;
  return <Pill tone="soft">Da fare</Pill>;
}

/** Visite dai clienti: si fissano, si registrano dal telefono, poi riassunto e azioni. */
export function VisitsPage({ ctx }: { ctx: Ctx }) {
  const { api } = ctx;
  const param = (ctx.pageParam ?? {}) as { newVisit?: VisitPrefill; open?: number };
  const [list, setList] = useState<VisitDto[] | null>(null);
  const [form, setForm] = useState<{ visit: VisitDto | null; prefill?: VisitPrefill } | null>(param.newVisit ? { visit: null, prefill: param.newVisit } : null);
  const [openId, setOpenId] = useState<number | null>(param.open ?? null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setList(await api.get<VisitDto[]>('/api/visits'));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (ctx.event?.type === 'visit.updated' || ctx.event?.type === 'agenda.changed') void load();
  }, [ctx.eventSeq]); // eslint-disable-line react-hooks/exhaustive-deps

  const today = todayKey();
  const upcoming = (list ?? []).filter((v) => v.status !== 'cancelled' && localDay(v.startsAt) >= today && v.aiStatus === 'none').sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  const past = (list ?? []).filter((v) => !upcoming.includes(v));

  return (
    <div className="panels">
      <Panel
        title="Visite e call"
        subtitle="Appuntamenti dai clienti e call: chi ci va, registrazione e riassunto"
        right={
          <button type="button" className="btn dark" title="Fissa una visita dal cliente o una call: finisce nell’agenda di chi partecipa" onClick={() => setForm({ visit: null })}>
            <Icon name="plus" size={16} /> Nuova visita o call
          </button>
        }
      >
        <ErrorBanner message={error} onClose={() => setError(null)} />
        <h3 style={{ margin: '4px 0 8px' }}>In programma</h3>
        {list && upcoming.length === 0 ? (
          <div className="muted small">Nessuna visita o call in programma.</div>
        ) : (
          <div className="ag-list">
            {upcoming.map((v) => (
              <VisitRow key={v.id} v={v} onOpen={() => setOpenId(v.id)} />
            ))}
          </div>
        )}
      </Panel>

      <Panel title="Fatte" subtitle="Ultimi due mesi">
        {list && past.length === 0 ? (
          <Empty icon="calendar">Ancora nessuna visita fatta.</Empty>
        ) : (
          <div className="ag-list">
            {past.map((v) => (
              <VisitRow key={v.id} v={v} onOpen={() => setOpenId(v.id)} />
            ))}
          </div>
        )}
      </Panel>

      {form && (
        <VisitFormModal
          ctx={ctx}
          visit={form.visit}
          prefill={form.prefill}
          onClose={() => setForm(null)}
          onSaved={(v) => {
            setForm(null);
            void load();
            setOpenId(v.id);
          }}
        />
      )}
      {openId !== null && !form && (
        <VisitDetailModal
          ctx={ctx}
          id={openId}
          onClose={() => setOpenId(null)}
          onEdit={(v) => setForm({ visit: v })}
          onChanged={() => void load()}
        />
      )}
    </div>
  );
}

function VisitRow({ v, onOpen }: { v: VisitDto; onOpen: () => void }) {
  const who = withWho(v);
  return (
    <div className={`ag-row visit${v.people.length > 1 ? ' together' : ''}${v.status === 'cancelled' ? ' done' : ''}`} onClick={onOpen}>
      <span className="ag-day">{dayLabel(localDay(v.startsAt))}</span>
      <span className="ag-time">{fmtTime(v.startsAt)}</span>
      <span className="ag-title">
        <Icon name={v.kind === 'call' ? 'phone' : 'building'} size={13} /> {v.title}
        {who && who !== v.title && <span className="muted"> · {who}</span>}
        {v.people.length === 1 && <span className="muted small"> · {v.people[0].name.split(' ')[0]}</span>}
      </span>
      {v.people.length > 1 && (
        <span className="ag-tag together" title="Da fare insieme">
          <Icon name="users" size={12} /> Insieme: {v.people.map((p) => p.name.split(' ')[0]).join(' e ')}
        </span>
      )}
      {aiPill(v)}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fissa / modifica visita

export function VisitFormModal({
  ctx,
  visit,
  prefill,
  day,
  onClose,
  onSaved,
}: {
  ctx: Ctx;
  visit: VisitDto | null;
  prefill?: VisitPrefill;
  day?: string;
  onClose: () => void;
  onSaved: (v: VisitDto) => void;
}) {
  const { api } = ctx;
  const [meta, metaError] = useVisitMeta(ctx);
  const start0 = visit ? new Date(visit.startsAt) : null;
  const end0 = visit?.endsAt ? new Date(visit.endsAt) : null;

  const [target, setTarget] = useState<VisitTarget | null>(null);
  const [search, setSearch] = useState('');
  const [title, setTitle] = useState(visit?.title ?? '');
  const [date, setDate] = useState(visit ? localDay(visit.startsAt) : day ?? todayKey());
  const [start, setStart] = useState(start0 ? hm(start0) : '');
  const [end, setEnd] = useState(end0 ? hm(end0) : '');
  const [kind, setKind] = useState<'visit' | 'call'>(visit?.kind ?? 'visit');
  const isCall = kind === 'call';
  const [location, setLocation] = useState(visit && visit.kind !== 'call' ? visit.location ?? '' : '');
  const [link, setLink] = useState(visit?.kind === 'call' ? visit.location ?? '' : '');
  // Viaggio di andata (il ritorno è uguale): di solito mezz'ora
  const [travel, setTravel] = useState<number>(visit ? visit.travelMin : 30);
  const [notes, setNotes] = useState(visit?.notes ?? '');
  const [people, setPeople] = useState<number[]>(visit ? visit.people.map((p) => p.id) : []);
  const cal = useBusy(api, people, visit?.id ?? null, date);
  const [remind, setRemind] = useState<number | null>(60);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const touchedTitle = useRef(!!visit);
  const inited = useRef(false);

  // Prima apertura: chi va (di solito tutti e due) e con chi (dalla visita o da dove si è partiti)
  useEffect(() => {
    if (!meta || inited.current) return;
    inited.current = true;
    if (!visit) setPeople(meta.people.map((p) => p.id));
    const ids: VisitPrefill = visit ?? prefill ?? {};
    const t =
      (ids.dealId && meta.targets.find((x) => x.kind === 'deal' && x.id === ids.dealId)) ||
      (ids.contactId && meta.targets.find((x) => x.kind === 'contact' && x.id === ids.contactId)) ||
      (ids.clientId && meta.targets.find((x) => x.kind === 'client' && x.id === ids.clientId)) ||
      null;
    if (t) pick(t, !visit);
  }, [meta]); // eslint-disable-line react-hooks/exhaustive-deps

  const autoTitle = (t: VisitTarget, k: 'visit' | 'call' = kind) => `${k === 'call' ? 'Call' : 'Visita'} ${t.name.split(' · ')[t.kind === 'contact' && t.name.includes(' · ') ? 1 : 0]}`;

  const chooseKind = (k: 'visit' | 'call') => {
    setKind(k);
    if (!touchedTitle.current && target) setTitle(autoTitle(target, k));
    // Durata di partenza: call 1 ora, visita 1 ora e mezza
    if (start) setEnd(addMin(start, k === 'call' ? 60 : 90));
  };

  const pick = (t: VisitTarget | null, fill = true) => {
    setTarget(t);
    setSearch('');
    if (!t || !fill) return;
    if (!touchedTitle.current) setTitle(autoTitle(t));
    if (t.address && !location.trim()) setLocation(t.address);
  };

  const found = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!meta || q.length < 1) return [];
    return meta.targets.filter((t) => t.name.toLowerCase().includes(q)).slice(0, 8);
  }, [meta, search]);

  const togglePerson = (id: number) => setPeople((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const save = async () => {
    if (!people.length) return setError('Scegli chi va');
    if (!start) return setError("Scegli l'orario");
    const s = new Date(`${date}T${start}`);
    if (Number.isNaN(s.getTime())) return setError("Ora d'inizio non valida");
    let e: Date | null = null;
    if (end) {
      e = new Date(`${date}T${end}`);
      if (e <= s) return setError("L'ora di fine deve essere dopo l'inizio");
    }
    const body = {
      // Il titolo non è obbligatorio: se manca lo ricavo da con chi si va
      // Anche «Visita» o «Call» da soli diventano «Visita <azienda>»: in agenda si deve capire con chi è
      title: (/^(visita|call|app\.?\s*to|appuntamento)?$/i.test(title.trim()) && target ? autoTitle(target) : title.trim()) || (isCall ? 'Call' : 'Visita'),
      kind,
      travelMin: isCall ? 0 : travel,
      clientId: target?.clientId ?? (visit && !target ? visit.clientId : null),
      contactId: target?.contactId ?? (visit && !target ? visit.contactId : null),
      dealId: target?.dealId ?? (visit && !target ? visit.dealId : null),
      location: (isCall ? link : location).trim() || null,
      startsAt: s.toISOString(),
      endsAt: e ? e.toISOString() : null,
      notes: notes.trim() || null,
      userIds: people,
      remindMin: remind,
    };
    setBusy(true);
    try {
      const v = visit ? await api.put<VisitDto>(`/api/visits/${visit.id}`, body) : await api.post<VisitDto>('/api/visits', body);
      onSaved(v);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  const current = target ? target.name : visit ? withWho(visit) : null;

  return (
    <Modal
      title={visit ? (isCall ? 'Modifica call' : 'Modifica visita') : isCall ? 'Nuova call' : 'Nuova visita'}
      onClose={onClose}
      footer={
        <div className="quote-actions">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Annulla
          </button>
          <button type="button" className="btn dark" onClick={() => void save()} disabled={busy || !meta}>
            {busy ? 'Salvataggio…' : visit ? 'Salva' : isCall ? 'Fissa la call' : 'Fissa la visita'}
          </button>
        </div>
      }
    >
      <ErrorBanner message={error ?? metaError} onClose={() => setError(null)} />
      <Field label="Cosa">
        <div className="vs-people">
          <button type="button" className={`btn small${!isCall ? ' dark' : ''}`} onClick={() => chooseKind('visit')}>
            <Icon name="building" size={14} /> Visita dal cliente
          </button>
          <button type="button" className={`btn small${isCall ? ' dark' : ''}`} onClick={() => chooseKind('call')}>
            <Icon name="phone" size={14} /> Call
          </button>
        </div>
      </Field>
      <Field label="Con chi" hint={isCall ? 'Cliente, trattativa o contatto della rubrica' : "Cliente, trattativa o contatto della rubrica: l'indirizzo arriva dalla rubrica"}>
        {current ? (
          <div className="row-actions" style={{ margin: 0 }}>
            <Pill tone="navy" icon={target?.kind === 'deal' ? 'layers' : target?.kind === 'contact' ? 'user' : 'building'}>
              {current}
            </Pill>
            <button type="button" className="btn ghost small" onClick={() => pick(null)}>
              Cambia
            </button>
          </div>
        ) : (
          <div className="vs-search">
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per nome…" autoFocus={!visit} />
            {found.length > 0 && (
              <div className="vs-results">
                {found.map((t) => (
                  <button type="button" key={`${t.kind}${t.id}`} onClick={() => pick(t)}>
                    <b>{t.name}</b>
                    <span className="muted small">
                      {t.sub}
                      {t.address ? ` · ${t.address}` : ''}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </Field>
      <Field label={isCall ? 'Chi partecipa' : 'Chi va'}>
        <div className="vs-people">
          {(meta?.people ?? []).map((p) => (
            <button type="button" key={p.id} className={`btn small${people.includes(p.id) ? ' dark' : ''}`} onClick={() => togglePerson(p.id)}>
              <Icon name={people.includes(p.id) ? 'check' : 'circle'} size={14} /> {p.name}
            </button>
          ))}
        </div>
      </Field>
      <div className="fields-3">
        <Field label="Data">
          <DateInput value={date} onChange={setDate} busy={cal.count} onMonth={cal.showMonth} />
        </Field>
        <Field label="Inizio">
          <TimeInput
            value={start}
            onChange={(v) => {
              setStart(v);
              if (!end || end <= v) setEnd(addMin(v, isCall ? 60 : 90));
            }}
          />
        </Field>
        <Field label="Fine">
          <TimeInput value={end} onChange={setEnd} />
        </Field>
      </div>
      {people.length > 0 && (
        <SlotPicker
          day={date}
          busy={cal.onDay(date)}
          start={start}
          end={end}
          defaultMinutes={isCall ? 60 : 90}
          travel={isCall ? 0 : travel}
          onDay={setDate}
          onPick={(s, e) => {
            setStart(s);
            setEnd(e);
          }}
        />
      )}
      {isCall ? (
        <Field label="Link della call" hint="Facoltativo: Meet, Zoom, Teams… Se è una telefonata lascialo vuoto">
          <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://meet.google.com/…" />
        </Field>
      ) : (
        <div className="fields-2">
          <Field label="Indirizzo">
            <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Via…, città" />
          </Field>
          <Field label="Viaggio, solo andata" hint={travelHint(start, end, travel)}>
            <select value={travel} onChange={(e) => setTravel(Number(e.target.value))}>
              {TRAVEL.map((t) => (
                <option key={t.v} value={t.v}>
                  {t.label}
                </option>
              ))}
            </select>
          </Field>
        </div>
      )}
      <More label="Titolo, promemoria e note" open={!!visit?.notes}>
        <div className="fields-2">
          <Field label="Titolo" hint={`Se lo lasci vuoto lo scrivo io, es. «${isCall ? 'Call' : 'Visita'} OMI Group»`}>
            <input
              value={title}
              onChange={(e) => {
                touchedTitle.current = true;
                setTitle(e.target.value);
              }}
              placeholder={isCall ? 'Call OMI Group' : 'Visita OMI Group'}
            />
          </Field>
          <Field label="Promemoria">
            <select value={remind ?? ''} onChange={(e) => setRemind(e.target.value === '' ? null : Number(e.target.value))}>
              {REMINDERS.map((r) => (
                <option key={String(r.v)} value={r.v ?? ''}>
                  {r.label}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Note">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Cosa proporre, cosa portare…" />
        </Field>
      </More>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Dettaglio: registrazione, riassunto, azioni successive

export function VisitDetailModal({
  ctx,
  id,
  onClose,
  onEdit,
  onChanged,
}: {
  ctx: Ctx;
  id: number;
  onClose: () => void;
  onEdit: (v: VisitDto) => void;
  onChanged: () => void;
}) {
  const { api } = ctx;
  const [meta] = useVisitMeta(ctx);
  const [v, setV] = useState<VisitDto | null>(null);
  const [transcript, setTranscript] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      setV(await api.get<VisitDto>(`/api/visits/${id}`));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [api, id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const ev = ctx.event;
    if (ev?.type === 'visit.updated' && ev.visitId === id) void load();
  }, [ctx.eventSeq]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mentre arriva o si lavora l'audio, controllo ogni tanto
  useEffect(() => {
    if (v?.aiStatus !== 'waiting') return;
    const t = setInterval(() => void load(), 8000);
    return () => clearInterval(t);
  }, [v?.aiStatus, load]);

  const act = async (fn: () => Promise<VisitDto | void>, after?: () => void) => {
    setBusy(true);
    setError(null);
    try {
      const r = await fn();
      if (r) setV(r);
      onChanged();
      after?.();
    } catch (e) {
      setError((e as Error).message);
    }
    setBusy(false);
  };

  const upload = async (f: File) => {
    if (f.size > 24 * 1024 * 1024) return setError('File troppo grande: massimo 24 MB (circa 50 minuti).');
    await act(async () => {
      const res = await fetch(`${api.baseUrl}/api/visits/${id}/audio-file`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${api.token}`, 'Content-Type': f.type || 'audio/mp4' },
        body: f,
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error ?? `Errore ${res.status}`);
      return data as VisitDto;
    });
  };

  const remove = async () => {
    if (!v || !(await ask(`Eliminare la visita "${v.title}"? Sparisce anche dall'agenda di chi doveva andare.`))) return;
    void act(() => api.del(`/api/visits/${id}`).then(() => undefined), onClose);
  };

  if (!v) {
    return (
      <Modal title="Visita" onClose={onClose}>
        <ErrorBanner message={error} onClose={() => setError(null)} />
        {!error && <div className="muted small">Caricamento…</div>}
      </Modal>
    );
  }

  const who = withWho(v);
  const end = v.endsAt ? ` – ${fmtTime(v.endsAt)}` : '';
  const aiOff = meta && (!meta.ai.transcribe || !meta.ai.summary);

  return (
    <Modal
      title={v.title}
      onClose={onClose}
      footer={
        <div className="quote-actions">
          <button type="button" className="btn ghost" onClick={remove} disabled={busy} title="Elimina la visita">
            <Icon name="trash" size={15} />
          </button>
          {v.status === 'planned' && (
            <button type="button" className="btn" title="Non si fa più: resta nell’elenco come annullata" onClick={() => void act(() => api.post<VisitDto>(`/api/visits/${id}/status`, { status: 'cancelled' }))} disabled={busy}>
              Annulla visita
            </button>
          )}
          {v.status === 'cancelled' && (
            <button type="button" className="btn" title="La visita torna in programma" onClick={() => void act(() => api.post<VisitDto>(`/api/visits/${id}/status`, { status: 'planned' }))} disabled={busy}>
              Ripristina
            </button>
          )}
          {v.status === 'planned' && (
            <button type="button" className="btn" title="Segna la visita come fatta" onClick={() => void act(() => api.post<VisitDto>(`/api/visits/${id}/status`, { status: 'done' }))} disabled={busy}>
              <Icon name="check" size={15} /> Fatta
            </button>
          )}
          <button type="button" className="btn dark" onClick={() => onEdit(v)} disabled={busy}>
            <Icon name="edit" size={15} /> Modifica
          </button>
        </div>
      }
    >
      <ErrorBanner message={error} onClose={() => setError(null)} />
      {done && (
        <div className="vs-ok small">
          <Icon name="check" size={14} /> {done}
        </div>
      )}
      <dl className="vs-kv">
        <dt>Quando</dt>
        <dd>
          {dayLabel(localDay(v.startsAt))}, {fmtTime(v.startsAt)}
          {end} {aiPill(v)}
        </dd>
        {who && (
          <>
            <dt>Con</dt>
            <dd>
              {who}
              {v.dealId && (
                <button type="button" className="btn ghost small" onClick={() => ctx.setPage('deals')}>
                  <Icon name="layers" size={13} /> Trattativa
                </button>
              )}
            </dd>
          </>
        )}
        <dt>{v.kind === 'call' ? 'Partecipa' : 'Va'}</dt>
        <dd>{v.people.map((p) => p.name).join(', ')}</dd>
        {v.kind !== 'call' && v.travelMin > 0 && (
          <>
            <dt>Viaggio</dt>
            <dd>
              {v.travelMin} minuti all'andata e {v.travelMin} al ritorno
            </dd>
          </>
        )}
        {v.kind === 'call' && v.location && (
          <>
            <dt>Link</dt>
            <dd>
              {/^https?:\/\//.test(v.location) ? (
                <a href={v.location} target="_blank" rel="noreferrer">
                  {v.location}
                </a>
              ) : (
                v.location
              )}
            </dd>
          </>
        )}
        {v.kind !== 'call' && v.location && (
          <>
            <dt>Dove</dt>
            <dd>
              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(v.location)}`} target="_blank" rel="noreferrer">
                {v.location}
              </a>
            </dd>
          </>
        )}
        {v.notes && (
          <>
            <dt>Note</dt>
            <dd style={{ whiteSpace: 'pre-wrap' }}>{v.notes}</dd>
          </>
        )}
      </dl>

      {/* ---- Registrazione ---- */}
      <h3 className="vs-h">
        <Icon name="mic" size={16} /> Registrazione
      </h3>
      {v.audio.recordings === 0 ? (
        <p className="muted small" title="Sul telefono Android apri la visita nell’app CRM TecnoSide e premi «Registra».">
          Registra dal telefono con l’app, oppure carica qui un file audio.
        </p>
      ) : (
        <p className="small">
          {v.audio.chunks} {v.audio.chunks === 1 ? 'pezzo' : 'pezzi'} di audio (circa {v.audio.chunks * 2} min), {v.audio.done} trascritti
          {v.audio.open ? ' · registrazione in corso' : ''}.
        </p>
      )}
      {v.aiStatus === 'waiting' && !v.audio.open && <p className="muted small">Trascrizione e riassunto in corso: di solito serve circa un minuto.</p>}
      {aiOff && v.aiStatus === 'waiting' && <p className="small vs-warn" title="Verrà lavorato appena il riassunto automatico sarà attivo.">Riassunto automatico non ancora attivo: l’audio resta salvato.</p>}
      {v.aiStatus === 'error' && <p className="small vs-warn">{v.aiError ?? 'Qualcosa non ha funzionato.'}</p>}
      <div className="row-actions">
        <input
          ref={fileRef}
          type="file"
          accept="audio/*,.m4a,.mp3,.wav,.ogg,.webm"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) void upload(f);
          }}
        />
        <button type="button" className="btn small" title="Una registrazione fatta con un altro programma (al massimo 24 MB, circa 50 minuti)" onClick={() => fileRef.current?.click()} disabled={busy}>
          <Icon name="paperclip" size={14} /> Carica file audio
        </button>
        {(v.aiStatus === 'error' || v.aiStatus === 'ready') && (
          <button type="button" className="btn small" title="Riascolta l’audio e scrive di nuovo il riassunto" onClick={() => void act(() => api.post<VisitDto>(`/api/visits/${id}/retry`))} disabled={busy}>
            <Icon name="refresh" size={14} /> Rifai il riassunto
          </button>
        )}
        {v.hasTranscript && (
          <button
            type="button"
            className="btn ghost small"
            title="Il testo di tutto quello che si è detto, parola per parola"
            onClick={() => {
              if (transcript !== null) return setTranscript(null);
              void api
                .get<{ text: string }>(`/api/visits/${id}/transcript`)
                .then((r) => setTranscript(r.text))
                .catch((e) => setError((e as Error).message));
            }}
          >
            <Icon name="note" size={14} /> {transcript !== null ? 'Nascondi trascrizione' : 'Trascrizione'}
          </button>
        )}
      </div>
      {transcript !== null && <div className="vs-transcript small">{transcript || 'Vuota.'}</div>}

      {/* ---- Riassunto e azioni ---- */}
      {v.ai && (
        <>
          <h3 className="vs-h">
            <Icon name="note" size={16} /> Riassunto
          </h3>
          <p className="vs-summary">{v.ai.summary}</p>
          {v.ai.points.length > 0 && (
            <>
              <div className="vs-label">Punti chiave</div>
              <ul className="vs-ul">
                {v.ai.points.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </>
          )}
          {v.ai.needs.length > 0 && (
            <>
              <div className="vs-label">Esigenze del cliente</div>
              <ul className="vs-ul">
                {v.ai.needs.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </>
          )}
          <AfterVisit
            ctx={ctx}
            v={v}
            people={meta?.people ?? v.people.map((p) => ({ ...p, role: '' }))}
            onApplied={(nv, msg) => {
              setV(nv);
              setDone(msg);
              onChanged();
            }}
          />
        </>
      )}
    </Modal>
  );
}

interface Row extends VisitAction {
  on: boolean;
}

/** Azioni proposte: si correggono e si applicano (promemoria in agenda, trattativa aggiornata). */
function AfterVisit({ ctx, v, people, onApplied }: { ctx: Ctx; v: VisitDto; people: Person[]; onApplied: (v: VisitDto, msg: string) => void }) {
  const { api } = ctx;
  const ai = v.ai!;
  const [rows, setRows] = useState<Row[]>(() => ai.actions.map((a) => ({ ...a, on: true })));
  const [stage, setStage] = useState<DealStage | ''>(ai.dealStage ?? '');
  const [note, setNote] = useState(() => [ai.dealNote, ai.summary].filter(Boolean).join('\n\n'));
  const [createDeal, setCreateDeal] = useState(!v.dealId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (i: number, p: Partial<Row>) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...p } : x)));

  const apply = async () => {
    const actions = rows.filter((r) => r.on && r.text.trim()).map(({ on: _on, ...a }) => ({ ...a, text: a.text.trim() }));
    const hasDeal = !!v.dealId || createDeal;
    setBusy(true);
    setError(null);
    try {
      const nv = await api.post<VisitDto>(`/api/visits/${v.id}/apply`, {
        actions,
        dealStage: hasDeal && stage ? stage : null,
        dealNote: hasDeal && note.trim() ? note.trim() : null,
        createDeal: !v.dealId && createDeal,
      });
      const parts = [`${actions.length} promemoria in agenda`];
      if (hasDeal) parts.push(v.dealId ? 'trattativa aggiornata' : 'trattativa creata');
      onApplied(nv, `Fatto: ${parts.join(', ')}.`);
    } catch (e) {
      setError((e as Error).message);
    }
    setBusy(false);
  };

  return (
    <div className="vs-after">
      <h3 className="vs-h">
        <Icon name="check" size={16} /> Dopo la visita
      </h3>
      {v.actionsAppliedAt && <p className="muted small">Già applicate il {dayLabel(localDay(v.actionsAppliedAt))}: se le applichi di nuovo si aggiungono altri promemoria.</p>}
      <ErrorBanner message={error} onClose={() => setError(null)} />
      <div className="vs-actions">
        {rows.map((r, i) => (
          <div key={i} className={`vs-action${r.on ? '' : ' off'}`}>
            <input type="checkbox" checked={r.on} onChange={(e) => set(i, { on: e.target.checked })} title="Togli la spunta per saltare questa azione" />
            <select value={r.kind} onChange={(e) => set(i, { kind: e.target.value as VisitActionKind })}>
              {KINDS.map((k) => (
                <option key={k.v} value={k.v}>
                  {k.label}
                </option>
              ))}
            </select>
            <input className="vs-text" value={r.text} onChange={(e) => set(i, { text: e.target.value })} />
            <DateInput value={r.dueDate ?? ''} onChange={(v) => set(i, { dueDate: v || null })} allowEmpty placeholder="Senza data" />
            <div className="vs-who">
              {people.map((p) => (
                <button
                  type="button"
                  key={p.id}
                  className={`btn icon small${r.userIds.includes(p.id) ? ' dark' : ''}`}
                  title={`${p.name}: ${r.userIds.includes(p.id) ? 'lo fa (clic per togliere)' : 'clic per assegnarla'}`}
                  onClick={() => set(i, { userIds: r.userIds.includes(p.id) ? r.userIds.filter((x) => x !== p.id) : [...r.userIds, p.id] })}
                >
                  {p.name.slice(0, 1)}
                </button>
              ))}
            </div>
          </div>
        ))}
        <button
          type="button"
          className="btn ghost small"
          onClick={() => setRows((r) => [...r, { text: '', kind: 'task', dueDate: null, userIds: v.people.map((p) => p.id), on: true }])}
        >
          <Icon name="plus" size={14} /> Aggiungi azione
        </button>
      </div>

      <div className="vs-label">Trattativa</div>
      {!v.dealId && (
        <label className="ag-allday">
          <input type="checkbox" checked={createDeal} onChange={(e) => setCreateDeal(e.target.checked)} /> Crea una trattativa per {v.clientName ?? v.contactName ?? 'questa visita'}
        </label>
      )}
      {(v.dealId || createDeal) && (
        <>
          <div className="fields-2">
            <Field label="Stato">
              <select value={stage} onChange={(e) => setStage(e.target.value as DealStage | '')}>
                <option value="">Non cambiare</option>
                {STAGES.map((s) => (
                  <option key={s.v} value={s.v}>
                    {s.label}
                  </option>
                ))}
              </select>
            </Field>
            {ai.valueEur !== null && (
              <Field label="Valore emerso">
                <input readOnly value={`${ai.valueEur.toLocaleString('it-IT')} €`} />
              </Field>
            )}
          </div>
          <Field label="Nota nella trattativa">
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} />
          </Field>
        </>
      )}
      <div className="row-actions">
        <button type="button" className="btn dark" title="Mette in agenda le azioni con la spunta e aggiorna la trattativa" onClick={() => void apply()} disabled={busy}>
          <Icon name="check" size={15} /> {busy ? 'Applico…' : 'Applica'}
        </button>
      </div>
    </div>
  );
}
